import { inject, Injectable, Injector } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { LoginResponse, OidcSecurityService } from "angular-auth-oidc-client";
import { MessageService } from "primeng/api";
import { forkJoin, map } from "rxjs";
import { AuthService } from "./core/auth/auth.service";
import { ErrorMessageService } from "./core/util/error-message.service";
import { WhiteLabelService } from "./core/util/white-label.service";
import { UserService } from "./user/shared/user.service";

@Injectable()
export class AppInitService {
  private wasAuthenticated: boolean;
  private authPending: boolean;
  private requestedPath: string;

  private oidcSecurityService = inject(OidcSecurityService);
  private userService = inject(UserService);
  private messageService = inject(MessageService);
  private errorMessageService = inject(ErrorMessageService);
  private whiteLabelService = inject(WhiteLabelService);
  private authService = inject(AuthService);
  private injector = inject(Injector);

  init(): Promise<void> {
    return new Promise<void>((resolve) => {
      console.debug(
        `AppInitService: start -> ${document.defaultView.location.toString()}`,
      );
      this.preCheckAuth();

      console.debug("App init: checking auth status...");
      return this.oidcSecurityService.checkAuth().subscribe({
        next: (loginResponse: LoginResponse) => {
          const isAuthenticated = loginResponse.isAuthenticated;
          console.debug(`...success! isAuthenticated=${isAuthenticated}`);

          // User is still authencatied after a failed logout request
          // Revoke local auth data and reload the app
          if (window.location.search.indexOf("force-logout") > -1) {
            console.debug(
              "...failed logout attempt detected, forcing local logout.",
            );
            this.userService.forceLogout();
            document.location.href = `${this.whiteLabelService.appUrl}/`;
          }

          if (isAuthenticated) {
            console.debug("App init: loading user info...");

            if (!this.wasAuthenticated && this.authPending) {
              const requestedPath: string =
                this.requestedPath && this.requestedPath.length > 0
                  ? this.requestedPath
                  : "dashboard";

              const resolvedPath = `/${requestedPath}`;
              console.debug(
                `...success! Reloading app with resolved path ${resolvedPath}...`,
              );
              window.location.href = resolvedPath;
            } else {
              this.userService.login().subscribe({
                next: () => {
                  console.debug("...success! Loading requested path...");
                  resolve();
                },
                error: () => {
                  console.debug("...error! Logging user off...");

                  // Call resolve so the app can be loaded to display the error messages
                  resolve();
                  this.forceLogOff();
                },
              });
            }
          } else {
            // Proceed with authentication flow
            resolve();
          }
        },
        error: (error) => {
          console.debug(`...error! ${error}`);
          setTimeout(() => {
            document.location.href = `${this.whiteLabelService.appUrl}/`;
          }, 500);
        },
        complete: () => {
          console.debug("AppInitService: end");
        },
      });
    });
  }

  private preCheckAuth(): void {
    forkJoin([
      this.oidcSecurityService.isAuthenticated(),
      this.oidcSecurityService.getState(),
    ])
      .pipe(
        map((responses) => {
          this.wasAuthenticated = responses[0];
          this.authPending =
            responses[1] !== undefined && responses[1].length > 0;
        }),
      )
      .subscribe();

    if (!this.wasAuthenticated) {
      this.authService.clearUserState();
    }
    if (!this.wasAuthenticated && !this.authPending) {
      this.oidcSecurityService.logoffLocal();
    }

    if (this.authPending) {
      const session: string = sessionStorage.getItem("main");
      // This is where oidc lib stores the original requested path
      // We have some apache rewrite aliases that need to be escaped here
      this.requestedPath = JSON.parse(session).redirect;
    }
  }

  private forceLogOff(): void {
    setTimeout(() => {
      this.messageService.add(
        this.errorMessageService.buildMessage(
          "error",
          this.injector.get(TranslateService).instant("Error"),
          this.injector
            .get(TranslateService)
            .instant("loadingUserProfileError"),
          "",
        ),
      );
    }, 300);
    setTimeout(() => {
      this.userService.logout();
    }, 3000);
  }
}
